import {
  IClassroomEnrollmentRejection,
  IClassroomEnrollmentsCreateResponse
} from 'interfaces/classrooms';
import { pluralize } from 'utils/pluralize';

export type EnrollmentResultNotificationType = 'success' | 'warning' | 'error';

export interface IEnrollmentResultNotification {
  type: EnrollmentResultNotificationType;
  message: string;
  description: string;
}

const REJECTION_REASON_LABELS: Record<string, string> = {
  already_enrolled_in_classroom: 'já está matriculado nesta turma'
};

const DEFAULT_REJECTION_LABEL = 'não pôde ser matriculado nesta turma';

const UNNAMED_STUDENT_LABEL = 'Aluno não identificado';

const isCreateResponse = (
  payload: unknown
): payload is IClassroomEnrollmentsCreateResponse =>
  typeof payload === 'object' &&
  payload !== null &&
  Array.isArray((payload as { data?: unknown }).data);

const enrolledMessage = (count: number) =>
  `${pluralize(count, 'aluno matriculado', 'alunos matriculados')} com sucesso`;

/**
 * "Fulano (já está matriculado nesta turma); Beltrano (...)" — the operator
 * needs the name to know which student to re-enroll in a separate classroom.
 */
export const describeRejections = (
  rejections: IClassroomEnrollmentRejection[]
): string =>
  rejections
    .map(({ user_name, reason }) => {
      const label = REJECTION_REASON_LABELS[reason] || DEFAULT_REJECTION_LABEL;

      return `${user_name || UNNAMED_STUDENT_LABEL} (${label})`;
    })
    .join('; ');

/**
 * Decides which notification the enrollment screen raises from the response of
 * `POST /admin/classrooms/:classroom_id/enrollments`. The endpoint answers
 * `201` even when every student is refused, so the three outcomes — all
 * enrolled, partially enrolled and none enrolled — are told apart here, by the
 * body, and never by the HTTP status.
 *
 * `requestedCount` is only used when the body does not follow the current
 * contract; see below.
 */
export const buildEnrollmentResultNotification = (
  payload: unknown,
  requestedCount: number
): IEnrollmentResultNotification => {
  if (!isCreateResponse(payload)) {
    // Backend still answering the previous contract (a bare array of
    // enrollments): report the old optimistic message instead of an invented
    // total refusal.
    return {
      type: 'success',
      message: enrolledMessage(requestedCount),
      description: 'Tudo certo!'
    };
  }

  const createdCount = payload.data.length;
  // Defensive: an older or partial body may omit the list entirely.
  const rejections = Array.isArray(payload.rejected) ? payload.rejected : [];

  if (rejections.length === 0) {
    return {
      type: 'success',
      message: enrolledMessage(createdCount),
      description: 'Tudo certo!'
    };
  }

  const description = `Não matriculados: ${describeRejections(rejections)}.`;

  if (createdCount === 0) {
    return {
      type: 'error',
      message: pluralize(
        rejections.length,
        'aluno não foi matriculado',
        'alunos não foram matriculados'
      ),
      description
    };
  }

  return {
    type: 'warning',
    message: `${pluralize(
      createdCount,
      'aluno matriculado',
      'alunos matriculados'
    )}, ${pluralize(rejections.length, 'recusado', 'recusados')}`,
    description
  };
};
